import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import FloatingBackButton from "./FloatingBackButton";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionTracker } from "@/hooks/useSession";
import { FlaskConical } from "lucide-react";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * A moldura de uma tela: largura, margem da barra lateral, o botão de voltar
 * flutuante e o selo do modo teste.
 *
 * A navbar e a barra lateral NÃO moram aqui: moram no Chrome, montado uma vez
 * no App, fora das telas — assim trocar de tela não desmonta o cromo. A
 * troca em si é uma View Transition do navegador (ver App.tsx): nada aqui
 * anima, e nada aqui começa invisível.
 */
const AppLayout = ({ children }: AppLayoutProps) => {
  const { t } = useTranslation();
  const { testerMode, setTesterMode, setTesterProducts, setTesterSubscriptionStatus } = useAuth();
  useSessionTracker();

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Celular: coluna de 520px centrada. Computador (≥901px): a barra
          lateral de 250px à esquerda e a coluna abre até 860px — os dois
          números moram em sistema.css (--barra-lateral, --coluna-pc). */}
      <div className="flex-1 flex flex-col min-w-0 com-barra-lateral">
        <div className="coluna-conteudo w-full max-w-[520px] mx-auto relative flex flex-col min-h-screen">
          <FloatingBackButton />
          {testerMode && (
            <button
              onClick={() => {
                setTesterMode(false);
                setTesterProducts([]);
                setTesterSubscriptionStatus(null);
              }}
              /* Selo do modo teste: --t-etiqueta (15px, o piso da régua para
                 rótulo) e a cor de frente do ouro, que existe nos dois temas.
                 Compacto e não um alvo de 56px porque ele é `fixed` no canto e
                 cobriria a BarraVoltar de toda tela. */
              className="fixed top-3 right-3 z-50 flex items-center gap-2 rounded-full px-4 py-2 font-bold active:scale-95 transition-transform"
              style={{
                backgroundColor: "hsl(var(--accent))",
                color: "hsl(var(--accent-foreground))",
                fontSize: "var(--t-etiqueta)",
              }}
            >
              <FlaskConical className="h-4 w-4" strokeWidth={2.4} />
              {t("tester.title")}
            </button>
          )}
          <main className="flex-1 w-full">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
